// Documents — the cross-deal library: every attachment and indexed link the
// firm has received, searchable by name, company, and CONTENT (chunk index).

import { useEffect, useRef, useState } from "react";
import { downloadDocument, fetchDocuments } from "../api";
import type { LibraryDoc } from "../types";
import { Ic } from "../components/Icon";
import { Logo } from "../components/Logo";

function fmtSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(raw: string | null): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function LibraryScreen({ onOpenDeal }: { onOpenDeal: (id: string) => void }) {
  const [docs, setDocs] = useState<LibraryDoc[] | null>(null);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const timer = useRef<number | null>(null);

  // Debounced fetch — content search hits the chunk index server-side.
  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setSearching(true);
      fetchDocuments(q.trim())
        .then(setDocs)
        .catch(() => setDocs([]))
        .finally(() => setSearching(false));
    }, q ? 250 : 0);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [q]);

  const open = (doc: LibraryDoc) => onOpenDeal(doc.dealSlug);

  return (
    <div className="page">
      <div className="topbar">
        <h1>Documents</h1>
        {docs && (
          <span style={{ color: "var(--ink-3)", fontSize: 13, marginLeft: 10 }}>
            · {docs.length} {q ? "matching" : "across all deals"}
          </span>
        )}
      </div>
      <div className="scroll" style={{ padding: "18px 28px 60px" }}>
        <div style={{ maxWidth: 980 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 16,
            padding: "9px 14px", background: "var(--surface)",
            border: "1px solid var(--border)", borderRadius: 8, maxWidth: 520,
          }}>
            <Ic name="search" size={14} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search names, companies — and inside documents…"
              style={{ border: "none", outline: "none", background: "none",
                       flex: 1, fontSize: 13.5, color: "var(--ink)" }}
              autoFocus
            />
            {searching && <Ic name="spinner" size={13} />}
          </div>

          {docs === null ? (
            <div style={{ color: "var(--ink-3)", fontSize: 13 }}>Loading…</div>
          ) : docs.length === 0 ? (
            <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
              {q ? "Nothing matches — try fewer words." :
                "No documents yet. Decks and attachments land here as deals come in."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {docs.map((doc, i) => (
                <div
                  key={`${doc.dealSlug}-${doc.name}-${i}`}
                  onClick={() => open(doc)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "9px 14px", background: "var(--surface)",
                    border: "1px solid var(--border)", borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  <Ic name={doc.kind === "link" ? "ext" : "file"} size={15} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500,
                                  overflow: "hidden", textOverflow: "ellipsis",
                                  whiteSpace: "nowrap" }}>
                      {doc.name}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--ink-3)", display: "flex",
                                  alignItems: "center", gap: 6, marginTop: 1 }}>
                      <Logo domain={doc.domain} co={doc.co} size={14} radius={3} />
                      {doc.co}
                      {doc.size ? <span>· {fmtSize(doc.size)}</span> : null}
                      {doc.date && <span>· {fmtDate(doc.date)}</span>}
                      {doc.indexed && (
                        <span title="Searchable in Knowledge"
                              style={{ color: "var(--pos)", fontWeight: 600 }}>· indexed</span>
                      )}
                    </div>
                  </div>
                  {doc.kind === "file" ? (
                    <button
                      title="Download"
                      onClick={(e) => { e.stopPropagation(); downloadDocument(doc.dealSlug, doc.name); }}
                      style={{ border: "none", background: "none", cursor: "pointer",
                               color: "var(--ink-3)", padding: 4, lineHeight: 0 }}
                    >
                      <Ic name="upload" size={14} />
                    </button>
                  ) : doc.url ? (
                    <a
                      href={doc.url} target="_blank" rel="noreferrer"
                      title={`Open on ${doc.host || "the web"}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: "var(--ink-3)", padding: 4, lineHeight: 0 }}
                    >
                      <Ic name="ext" size={14} />
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
